// src/components/ModeSelector.tsx

import React from 'react';
import { motion } from 'framer-motion';
import NavigatorNav from './NavigatorNav';

interface ModeSelectorProps {
  onSelectMode: (mode: 'validate' | 'gettingStarted') => void;
}

const ModeSelector: React.FC<ModeSelectorProps> = ({ onSelectMode }) => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <NavigatorNav />
      <div className="py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-5xl font-bold text-gray-900 mb-3">
            HIPAA Compliance Navigator
          </h1>
          <p className="text-base text-blue-600 font-semibold uppercase tracking-wide mb-2">
            An <a href="https://theinfinitra.com" target="_blank" rel="noopener noreferrer" className="text-blue-700 underline hover:text-blue-900">Infinitra</a> Innovations Initiative
          </p>
          <p className="text-sm text-gray-600 mb-4">
            Built by Ex-AWS Leaders: Saida Babu Chanda & Yogananda Karra
          </p>
          <p className="text-lg text-gray-500">
            Comprehensive assessment covering all HIPAA requirements
          </p>
        </motion.div>

        {/* Mode Cards */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {/* Validate Mode Card */}
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            onClick={() => onSelectMode('validate')}
            className="bg-white p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-blue-500 text-left group"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="text-5xl">🔍</div>
              <span className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-xs font-bold">
                URGENT
              </span>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition">
              Validate My Compliance
            </h2>

            <p className="text-gray-600 mb-6 text-lg">
              I have ePHI systems deployed and want to check for gaps
            </p>

            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-700 mb-2 font-medium">Perfect for:</p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Existing healthcare technology companies</li>
                <li>• Organizations facing enterprise audits</li>
                <li>• Teams responding to compliance questionnaires</li>
              </ul>
            </div>

            <div className="space-y-3 mb-6">
              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div>
                  <span className="font-medium text-gray-900">50 comprehensive questions</span>
                  <p className="text-sm text-gray-600">Adaptive branching based on your systems</p>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div>
                  <span className="font-medium text-gray-900">Compliance score (0-100)</span>
                  <p className="text-sm text-gray-600">Know exactly where you stand</p>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div>
                  <span className="font-medium text-gray-900">Critical gap analysis</span>
                  <p className="text-sm text-gray-600">Identify risks that could trigger audits</p>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div>
                  <span className="font-medium text-gray-900">30-day remediation roadmap</span>
                  <p className="text-sm text-gray-600">Week-by-week plan to fix gaps</p>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
                  </svg>
                  <span>Time to complete:</span>
                </div>
                <span className="font-bold text-gray-900">15 minutes</span>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-center">
              <span className="text-blue-600 font-semibold group-hover:underline">
                Start Assessment →
              </span>
            </div>
          </motion.button>

          {/* Getting Started Mode Card */}
          <motion.button
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            onClick={() => onSelectMode('gettingStarted')}
            className="bg-white p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-blue-500 text-left group"
          >
            <div className="flex items-start justify-between mb-4">
              <svg className="w-14 h-14 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clip-rule="evenodd"/>
              </svg>
              <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-bold">
                PLANNING
              </span>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition">
              Guide Me From Scratch
            </h2>

            <p className="text-gray-600 mb-6 text-lg">
              I'm planning/building ePHI systems and need step-by-step guidance
            </p>

            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-700 mb-2 font-medium">Perfect for:</p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Startups building healthcare technology</li>
                <li>• Teams in pre-launch or MVP stage</li>
                <li>• Organizations planning HIPAA compliance from Day 1</li>
              </ul>
            </div>

            <div className="space-y-3 mb-6">
              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div>
                  <span className="font-medium text-gray-900">37 planning-focused questions</span>
                  <p className="text-sm text-gray-600">About your architecture and tech stack</p>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div>
                  <span className="font-medium text-gray-900">4-month implementation plan</span>
                  <p className="text-sm text-gray-600">Phased roadmap from foundation to audit-ready</p>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div>
                  <span className="font-medium text-gray-900">Customized to your stack</span>
                  <p className="text-sm text-gray-600">AWS/Azure/GCP specific guidance</p>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div>
                  <span className="font-medium text-gray-900">Templates + guides included</span>
                  <p className="text-sm text-gray-600">CloudFormation, policies, checklists</p>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
                  </svg>
                  <span>Time to complete:</span>
                </div>
                <span className="font-bold text-gray-900">12-15 minutes</span>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-center">
              <span className="text-blue-600 font-semibold group-hover:underline">
                Start Planning →
              </span>
            </div>
          </motion.button>
        </div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center text-sm text-gray-500"
        >
          <p className="mb-2">
            🔒 Your responses are private and used only to generate your personalized roadmap
          </p>
          <p>
            Based on official HIPAA regulations (45 CFR Part 164) and NIST SP 800-66r2
          </p>
        </motion.div>
      </div>
      </div>
    </div>
  );
};

export default ModeSelector;
